import React from 'react';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { COLORS } from '../../constants/styles';
import { WarningIcon } from '../../components/icons/WarningIcon';
import { InfoIconOutlined } from '../../components/icons/InfoIconOutlined';
import { IErrorMessage } from './errorMessage.type';
import ErrorMessageHelpers from './errorMessageHelpers';

export enum IconType {
    WARNING = 'warning',
    INFO = 'info',
}

export interface IErrorMessageProps {
    message: IErrorMessage;
    dataQaId?: string;
    className?: string;
    iconType?: IconType;
}

const ErrorMessageWrapper = styled.div`
    color: ${COLORS.error};
    font-size: 0.75rem;
    margin-top: 2px;
    margin-left: 0.625rem;
    display: flex;
    align-items: center;
`;

const WarningIconWrapper = styled.div`
    svg {
        margin-top: 2px;
    }
`;

const RenderMessageWrapper = styled.div`
    vertical-align: middle;
    line-height: 20px;
    margin-left: 0.5em;
`;

const WarningMessageWrapper = styled(ErrorMessageWrapper)`
    color: ${COLORS.warning};
`;

const ErrorMessage = ({
    dataQaId,
    className,
    message,
    iconType = IconType.WARNING,
}: IErrorMessageProps) => {
    const renderMessage = () => {
        if (ErrorMessageHelpers.isFormattedMessageProps(message)) {
            return TranslationHelpers.getFormattedMessage(message.id, message.values);
        }

        if (ErrorMessageHelpers.isMessageDescriptor(message)) {
            return TranslationHelpers.getFormattedMessage(message.id);
        }

        if (ErrorMessageHelpers.isString(message)) {
            return message;
        }

        if (ErrorMessageHelpers.isFormikError(message)) {
            return Object.values(message)[0];
        }
    };

    const isInfoIcon = iconType === IconType.INFO;
    const iconColor = isInfoIcon ? COLORS.warning : COLORS.error;

    const renderIcon = () => {
        if (isInfoIcon) {
            return <InfoIconOutlined color={iconColor} />;
        }
        return <WarningIcon color={iconColor} />;
    };

    const Wrapper = isInfoIcon ? WarningMessageWrapper : ErrorMessageWrapper;

    return (
        <Wrapper className={`error ${className || ''}`}>
            <WarningIconWrapper>{renderIcon()}</WarningIconWrapper>
            <RenderMessageWrapper data-qa-id={dataQaId} role="status">
                {renderMessage()}
            </RenderMessageWrapper>
        </Wrapper>
    );
};

export default ErrorMessage;
